import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Space, Tabs, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';

const { Title, Text } = Typography;

export interface DetailTab {
  key: string;
  label: string;
  content: ReactNode;
  /** Renders the tab greyed out — used for modules that arrive in a later phase. */
  disabled?: boolean;
}

export interface DetailPageProps {
  title: string;
  /** The business key: customer number, job number, chassis number. */
  subtitle?: string;
  tags?: { label: string; colour?: string }[];
  actions?: ReactNode;
  tabs: DetailTab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  onBack?: () => void;
}

/**
 * The second screen primitive (CLAUDE.md §10): header, status, tabs.
 *
 * The customer and motorcycle detail pages are the spine of the product — every
 * module contributes a tab. Phase 0 ships the empty tabs as disabled rather than
 * hiding them, so the shape of the finished product is visible from the first
 * demo and nobody is surprised by where their module lands.
 */
export function DetailPage({
  title,
  subtitle,
  tags = [],
  actions,
  tabs,
  activeTab,
  onTabChange,
  onBack,
}: DetailPageProps): ReactNode {
  return (
    <Flex vertical gap={16}>
      <Card size="small">
        <Flex align="flex-start" justify="space-between" wrap gap={12}>
          <Flex vertical gap={4}>
            <Space size={8} wrap>
              {onBack !== undefined && <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} />}
              <Title level={4} style={{ margin: 0 }}>
                {title}
              </Title>
            </Space>
            <Space size={8} wrap style={{ paddingLeft: onBack !== undefined ? 40 : 0 }}>
              {subtitle !== undefined && <Text type="secondary">{subtitle}</Text>}
              {tags.map((tag) => (
                <Tag key={tag.label} color={tag.colour}>
                  {tag.label}
                </Tag>
              ))}
            </Space>
          </Flex>
          <Space wrap>{actions}</Space>
        </Flex>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        items={tabs.map((tab) => ({
          key: tab.key,
          label: tab.label,
          disabled: tab.disabled ?? false,
          children: tab.content,
        }))}
      />
    </Flex>
  );
}
